import { useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../utils/auth";
import { sdkWrapperURL } from "../utils/api-url-list";
import { ReactComponent as Loader } from "../assets/icons/Loader.svg";
import { ReactComponent as BackArrow } from "../assets/icons/BackArrow.svg";
import { buildScanRouteState } from "../utils/scanDuration";

const questions = [
  {
    key: "has_medical_conditions",
    text: "Have you been diagnosed with any medical conditions such as diabetes, hypertension, asthma, heart disease, thyroid disorders, or any other chronic illness?",
    type: "yesno",
    icon: (
      <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
      </svg>
    ),
  },
  {
    key: "on_medication",
    text: "Are you going under any kind of medication for any disease?",
    type: "yesno",
    icon: (
      <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M19.428 15.428a2 2 0 00-1.022-.547l-2.387-.477a6 6 0 00-3.86.517l-.318.158a6 6 0 01-3.86.517L6.05 15.21a2 2 0 00-1.806.547M8 4h8l-1 1v5.172a2 2 0 00.586 1.414l5 5c1.26 1.26.367 3.414-1.415 3.414H4.828c-1.782 0-2.674-2.154-1.414-3.414l5-5A2 2 0 009 10.172V5L8 4z" />
      </svg>
    ),
  },
  {
    key: "sleep_pattern",
    text: "What is your sleep pattern?",
    type: "choice",
    options: ["4-6 hours", "6-8 hours", ">=8 hours"],
    icon: (
      <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z" />
      </svg>
    ),
  },
  {
    key: "smokes_tobacco",
    text: "Do you smoke or use tobacco products?",
    type: "yesno",
    icon: (
      <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636" />
      </svg>
    ),
  },
  {
    key: "consumes_alcohol",
    text: "Do you consume alcohol?",
    type: "yesno",
    icon: (
      <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M19.428 15.428a2 2 0 00-1.022-.547l-2.387-.477a6 6 0 00-3.86.517l-.318.158a6 6 0 01-3.86.517L6.05 15.21a2 2 0 00-1.806.547M8 4h8l-1 1v5.172a2 2 0 00.586 1.414l5 5c1.26 1.26.367 3.414-1.415 3.414H4.828c-1.782 0-2.674-2.154-1.414-3.414l5-5A2 2 0 009 10.172V5L8 4z" />
      </svg>
    ),
  },
];

const HealthQuestionnaire = () => {
  const navigate = useNavigate();
  const { state } = useLocation();
  const {
    getToken,
    userData: { employee_id },
    agentInfo,
  } = useAuth();

  const [answers, setAnswers] = useState({
    has_medical_conditions: null,
    on_medication: null,
    sleep_pattern: null,
    smokes_tobacco: null,
    consumes_alcohol: null,
  });
  const [submitting, setSubmitting] = useState(false);

  const allAnswered = Object.values(answers).every((v) => v !== null);

  const handleSubmit = async () => {
    if (!allAnswered) return;
    try {
      setSubmitting(true);

      if (!state?.userData?.phone_number) {
        alert("Phone number is missing. Please go back and enter your phone number.");
        setSubmitting(false);
        return;
      }

      const limitResp = await fetch("/api/check-scan-limit", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ phone_number: state?.userData?.phone_number }),
      });
      const limitData = await limitResp.json();
      if (!limitData?.success) {
        alert(limitData?.message || "Unable to verify scan limit. Please try again.");
        setSubmitting(false);
        return;
      }
      if (limitData?.data?.limit_reached) {
        alert("Maximum scan limit (10) reached for this phone number. No more scans are allowed.");
        setSubmitting(false);
        return;
      }

      let questionnaireId = null;
      try {
        const qResp = await fetch("/api/health-questionnaire", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            phone_number: state?.userData?.phone_number || "",
            referral_code: agentInfo?.code || "",
            scan_id: "",
            ...answers,
          }),
        });
        const qData = await qResp.json();
        if (qData?.success && qData?.data?.id) {
          questionnaireId = qData.data.id;
        }
      } catch (e) {
        console.error("Failed to save questionnaire:", e);
      }

      const token = await getToken();
      const scanTokenResp = await fetch(sdkWrapperURL("/users/sdk/test_api_private"), {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: token },
        body: JSON.stringify({ action: "create_scan_UL", employee_id }),
      });
      const scanTokenRespJSON = await scanTokenResp.json();
      if (scanTokenRespJSON?.statusCode?.toString().startsWith("2"))
        navigate(`/${state?.scanType}-scan`, {
          state: buildScanRouteState(state, {
            posture: "resting",
            scanToken: scanTokenRespJSON.scan_token,
            questionnaire: answers,
            questionnaireId,
          }),
        });
      else throw new Error(scanTokenRespJSON?.message ?? "Error in Fetching the Scan Token");
    } catch (err) {
      console.error(err);
      alert(err.message || "Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section className="min-h-screen bg-gray-50">
      <div className="bg-gradient-to-r from-[#1845a3] to-[#ED1C24] px-5 pt-5 pb-10">
        <div className="flex items-center">
          <button className="flex-shrink-0 p-2 pr-4" type="button" onClick={() => navigate(-1)}>
            <BackArrow className="h-3.5 w-3.5 text-white" />
          </button>
          <div>
            <h3 className="text-sm font-semibold text-white tracking-wide">Health Questionnaire</h3>
            <p className="mt-0.5 text-white/60 text-xxs">Please answer before starting your scan</p>
          </div>
        </div>
      </div>

      <div className="px-5 -mt-6">
        <div className="rounded-2xl bg-white shadow-lg border border-gray-100 overflow-hidden">
          <div className="p-4 space-y-3">
            {questions.map((q, idx) => (
              <div key={q.key} className="rounded-xl border border-gray-100 bg-gray-50 overflow-hidden">
                <div className="p-3.5">
                  <div className="flex items-start space-x-3">
                    <div className="shrink-0 w-8 h-8 rounded-lg bg-gradient-to-br from-[#1845a3] to-[#ED1C24] flex items-center justify-center text-white shadow-sm">
                      {q.icon}
                    </div>
                    <p className="flex-1 text-xs text-gray-700 leading-relaxed">{q.text}</p>
                  </div>
                  <div className="flex items-center space-x-3 mt-3 ml-11">
                    {q.type === "choice" ? (
                      q.options.map((opt) => (
                        <button
                          key={opt}
                          type="button"
                          onClick={() => setAnswers((prev) => ({ ...prev, [q.key]: opt }))}
                          className={`flex-1 py-2 rounded-lg text-xs font-semibold transition-all duration-200 ${
                            answers[q.key] === opt
                              ? "bg-gradient-to-r from-[#1845a3] to-[#ED1C24] text-white shadow-md"
                              : "bg-white border border-gray-200 text-gray-500"
                          }`}
                        >
                          {opt}
                        </button>
                      ))
                    ) : (
                      <>
                        <button
                          type="button"
                          onClick={() => setAnswers((prev) => ({ ...prev, [q.key]: true }))}
                          className={`flex-1 py-2 rounded-lg text-xs font-semibold transition-all duration-200 ${
                            answers[q.key] === true
                              ? "bg-gradient-to-r from-[#1845a3] to-[#ED1C24] text-white shadow-md"
                              : "bg-white border border-gray-200 text-gray-500"
                          }`}
                        >
                          Yes
                        </button>
                        <button
                          type="button"
                          onClick={() => setAnswers((prev) => ({ ...prev, [q.key]: false }))}
                          className={`flex-1 py-2 rounded-lg text-xs font-semibold transition-all duration-200 ${
                            answers[q.key] === false
                              ? "bg-gradient-to-r from-[#1845a3] to-[#ED1C24] text-white shadow-md"
                              : "bg-white border border-gray-200 text-gray-500"
                          }`}
                        >
                          No
                        </button>
                      </>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div className="px-4 pb-5">
            <button
              type="button"
              onClick={handleSubmit}
              disabled={!allAnswered || submitting}
              className="w-full flex items-center justify-center space-x-2 py-3 rounded-xl bg-gradient-to-r from-[#1845a3] to-[#ED1C24] disabled:opacity-50 text-white shadow-lg transition-opacity"
            >
              <span className="text-xs font-semibold">Start Scan</span>
              {submitting && <Loader className="flex-shrink-0 h-4 w-4 animate-spin" />}
            </button>
          </div>
        </div>
      </div>
    </section>
  );
};

export default HealthQuestionnaire;
